import {memo, shapeComponent, ShapeComponent, StyleSheet, Text, useBreakpoint, useI18n, useStyles, View} from "shared/base"
import ActiveStep from "./active-step"
import AppHistory from "shared/history"
import Button from "components/chalky-layout/button"
import ButtonUnderscore from "components/chalky-layout/button-underscore"
import ChalkyLayout from "components/chalky-layout"
import {FlashNotifications as FlashMessage} from "flash-notifications"
import React from "react"
import RoleCard from "./role-card"
import Routes from "shared/routes"
import setLayout from "shared/set-layout"
import stepLayoutStyles from "./step-layout-styles"
import StripedPressable from "components/chalky-layout/striped-pressable"

const styles = StyleSheet.create({
  buttonContainer: {
    flexDirection: "row"
  },
  buttonContainerSmDown: {
    marginBottom: 32
  },
  buttonContainerMdUp: {
    justifyContent: "end"
  },

  roleCardsContainer: {},
  roleCardsContainerXlUp: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between"
  },

  roleCard: {},
  roleCardLgDown: {
    marginBottom: 15
  },
  roleCardXlUp: {
    marginBottom: 15,
    width: "49%"
  },

  skipThisStepButtonContainer: {
    marginRight: 10
  },
  skipThisStepButtonContainerSmDown: {
    flex: 1,
    marginRight: 10
  },

  continueButtonContainer: {},
  continueButtonContainerSmDown: {
    flex: 1,
    marginLeft: 10
  }
})

/** @typedef {object} RoutesUserCreateUserStep1Props */

/**
 * @typedef {object} RoutesUserCreateUserStep1State
 * @property {unknown} activeCard
 * @property {boolean} readMore
 */

/**
 * @extends {ShapeComponent<RoutesUserCreateUserStep1Props, RoutesUserCreateUserStep1State>}
 */
class RoutesUserCreateUserStep1 extends ShapeComponent {
  /** @type {RoutesUserCreateUserStep1State} */
  state = {
    activeCard: null,
    readMore: false
  }

  setup() {
    setLayout("front")

    const {t} = useI18n({namespace: "js.routes.user.create_user.step1"})

    this.breakpoint = useBreakpoint()
    this.xsUp = this.breakpoint.xsUp
    this.xsDown = this.breakpoint.xsDown
    this.smUp = this.breakpoint.smUp
    this.smDown = this.breakpoint.smDown
    this.mdUp = this.breakpoint.mdUp
    this.mdDown = this.breakpoint.mdDown
    this.lgUp = this.breakpoint.lgUp
    this.lgDown = this.breakpoint.lgDown
    this.xlUp = this.breakpoint.xlUp
    this.xlDown = this.breakpoint.xlDown
    this.xxlUp = this.breakpoint.xxlUp
    this.xxlDown = this.breakpoint.xxlDown
    this.windowWidth = this.breakpoint.windowWidth
    this.windowHeight = this.breakpoint.windowHeight
    this.t = t
  }

  render() {
    const {mdUp, smDown, t} = this.tt
    const {activeCard, readMore} = this.s
    const buttonContainerStyle = useStyles(styles, "buttonContainer")
    const continueButtonContainerStyle = useStyles(styles, "continueButtonContainer")
    const skipThisStepButtonContainerStyle = useStyles(styles, "skipThisStepButtonContainer")
    const rootContainerStyle = useStyles(stepLayoutStyles, "rootContainer")
    const yourRoleColumnStyle = useStyles(stepLayoutStyles, "yourRoleColumn")
    const rolesContainerStyle = useStyles(stepLayoutStyles, "rolesContainer")
    const yourRoleStyle = useStyles(stepLayoutStyles, "yourRole")
    const yourRoleDescriptionStyle = useStyles(stepLayoutStyles, "yourRoleDescription")
    const yourRoleWithDescriptionStyle = useStyles(stepLayoutStyles, "yourRoleWithDescription")
    const roleCardsContainerStyle = useStyles(styles, "roleCardsContainer")
    const roleCardStyle = useStyles(styles, "roleCard")

    const inactiveSteps = () => (
      <View testID="create-user-step1-inactive-steps">
        <ActiveStep description={t(".your_preferences")} label={t(".step_2")} testID="create-user-step1-preferences-step" />
        <ActiveStep description={t(".youre_ready")} label={t(".step_3")} testID="create-user-step1-ready-step" />
      </View>
    )

    return (
      <ChalkyLayout dataSet={this.layoutDataSet ||= {route: "user/create-user/step1"}} layout="front" maxWidth={1260} requireUser={false}>
        <View
          dataSet={this.rootContainerDataSet ||= {class: "root-container"}}
          style={rootContainerStyle}
          testID="routes/user/create-user/step1-route"
        >
          <View
            dataSet={this.yourRoleColumnDataSet ||= {class: "your-role-column"}}
            style={yourRoleColumnStyle}
            testID="create-user-step1-introduction"
          >
            <ActiveStep active label={t(".step_1")} testID="create-user-step1-current-step" />
            <View style={yourRoleWithDescriptionStyle} testID="create-user-step1-introduction-content">
              <Text
                dataSet={this.yourRoleDataSet ||= {class: "your-role"}}
                style={yourRoleStyle}
                testID="create-user-step1-title"
              >
                {t(".your_role")}
              </Text>
              {!mdUp &&
                <View style={stepLayoutStyles.mb15} testID="create-user-step1-read-more-container">
                  <StripedPressable onPress={this.tt.onReadMorePress}>
                    <Text testID="create-user-step1-read-more-text">
                      {readMore ? t(".read_less") : t(".read_more")}
                    </Text>
                  </StripedPressable>
                </View>
              }
              {(mdUp || readMore) &&
                <Text
                  dataSet={this.yourRoleDescriptionDataSet ||= {class: "your-role-description"}}
                  style={yourRoleDescriptionStyle}
                  testID="create-user-step1-description"
                >
                  {t(".your_role_description")}
                </Text>
              }
            </View>
            {mdUp && inactiveSteps()}
          </View>
          <View
            dataSet={this.rolesContainerDataSet ||= {class: "roles-container"}}
            style={rolesContainerStyle}
            testID="create-user-step1-roles"
          >
            <View style={roleCardsContainerStyle} testID="create-user-step1-role-cards">
              <View style={roleCardStyle} testID="create-user-step1-teacher-card-container">
                <RoleCard
                  active={activeCard == "teacher"}
                  header={t(".teacher")}
                  icon="chalkboard-user"
                  iconVersion="FontAwesome6"
                  identifier="teacher"
                  onCardPress={this.tt.onTeacherCardPress}
                />
              </View>
              <View style={roleCardStyle} testID="create-user-step1-principal-administrator-card-container">
                <RoleCard
                  active={activeCard == "principal-administrator"}
                  header={t(".principal_administrator")}
                  icon="school"
                  iconVersion="FontAwesome5"
                  identifier="principal-administrator"
                  onCardPress={this.tt.onPrincipalAdministratorCardPress}
                />
              </View>
              <View style={roleCardStyle} testID="create-user-step1-well-being-coordinator-card-container">
                <RoleCard
                  active={activeCard == "well-being-coordinator"}
                  header={t(".well_being_coordinator")}
                  icon="hand-holding-heart"
                  iconVersion="FontAwesome5"
                  identifier="well-being-coordinator"
                  onCardPress={this.tt.onWellBeingCoordinatorCardPress}
                />
              </View>
              <View style={roleCardStyle} testID="create-user-step1-other-card-container">
                <RoleCard
                  active={activeCard == "other"}
                  header={t(".other")}
                  icon="person"
                  iconVersion="FontAwesome6"
                  identifier="other"
                  onCardPress={this.tt.onOtherCardPress}
                />
              </View>
            </View>
            <View style={buttonContainerStyle} testID="create-user-step1-buttons">
              <View style={skipThisStepButtonContainerStyle} testID="create-user-step1-skip-button-container">
                <ButtonUnderscore
                  dataSet={this.skipThisStepButtonDataSet ||= {class: "skip-button"}}
                  onPress={this.tt.onSkipThisStepPress}
                  primary
                  testID="create-user-step1-skip-button"
                  title={t(".skip_this_step")}
                />
              </View>
              <View style={continueButtonContainerStyle} testID="create-user-step1-continue-button-container">
                <Button
                  dataSet={this.continueButtonDataSet ||= {class: "continue-button"}}
                  onPress={this.tt.onContinuePress}
                  primary
                  testID="create-user-step1-continue-button"
                  title={t(".continue")}
                />
              </View>
            </View>
          </View>
          {smDown && inactiveSteps()}
        </View>
      </ChalkyLayout>
    )
  }

  onContinuePress = () => {
    const {t} = this.tt
    const {activeCard} = this.s

    if (!activeCard) {
      FlashMessage.alert(t(".please_choose_a_role"))

      return
    }

    AppHistory.push(Routes.userCreateUserStep2Path({role: activeCard}))
  }

  onOtherCardPress = () => this.triggerActiveCard("other")
  onPrincipalAdministratorCardPress = () => this.triggerActiveCard("principal-administrator")
  onReadMorePress = () => this.setState({readMore: !this.s.readMore})
  onSkipThisStepPress = () => AppHistory.push(Routes.userCreateUserStep2Path())
  onTeacherCardPress = () => this.triggerActiveCard("teacher")
  onWellBeingCoordinatorCardPress = () => this.triggerActiveCard("well-being-coordinator")

  triggerActiveCard(cardIdentifier) {
    const {activeCard} = this.s

    if (cardIdentifier == activeCard) {
      this.setState({activeCard: null})
    } else {
      this.setState({activeCard: cardIdentifier})
    }
  }
}

export default memo(shapeComponent(RoutesUserCreateUserStep1))
